import { useJobData, useProjectData } from "@//modules/inbox/utils";

import { getItemFromCollection } from "directus/src/operations";
import { useEffect } from "react";
import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useState } from "react";

export function useNotificationData(notifications) {
  const projectIds = useMemo(
    () => [
      ...new Set(
        notifications
          .filter((n) => n.collection === "projects")
          .map((n) => n.item)
          .filter(Boolean),
      ),
    ],
    [notifications],
  );

  const jobIds = useMemo(
    () => [
      ...new Set(
        notifications
          .filter((n) => n.collection === "jobs")
          .map((n) => n.item)
          .filter(Boolean),
      ),
    ],
    [notifications],
  );

  // Tickets do not require API calls for URL formation; just map id
  const ticketIds = useMemo(
    () => [
      ...new Set(
        notifications
          .filter((n) => n.collection === "tickets")
          .map((n) => n.item)
          .filter(Boolean),
      ),
    ],
    [notifications],
  );

  const billableIds = useMemo(
    () => [
      ...new Set(
        notifications
          .filter((n) => n.collection === "billables")
          .map((n) => n.item)
          .filter(Boolean),
      ),
    ],
    [notifications],
  );

  const jobExtensionIds = useMemo(
    () => [
      ...new Set(
        notifications
          .filter((n) => n.collection === "job_extensions")
          .map((n) => n.item)
          .filter(Boolean),
      ),
    ],
    [notifications],
  );

  const projectQueries = useQueries({
    queries: projectIds.map((id) => ({
      queryKey: ["notifications", "projects", id],
      queryFn: () =>
        getItemFromCollection("projects", id, { fields: ["project_id"] }),
      enabled: !!id,
      staleTime: 0,
      cacheTime: 0,
    })),
  });

  const jobQueries = useQueries({
    queries: jobIds.map((id) => ({
      queryKey: ["notification˝s", "jobs", id],
      queryFn: () =>
        getItemFromCollection("jobs", id, {
          fields: ["id", "project.project_id"],
        }),
      enabled: !!id,
      staleTime: 0,
      cacheTime: 0,
    })),
  });

  const jobExtensionQueries = useQueries({
    queries: jobExtensionIds.map((id) => ({
      queryKey: ["notifications", "job_extensions", id],
      queryFn: () =>
        getItemFromCollection("job_extensions", id, {
          fields: ["id", "job.id", "job.project.project_id"],
        }),
      enabled: !!id,
      staleTime: 0,
      cacheTime: 0,
    })),
  });

  const dataMap = useMemo(() => {
    const map = {};

    projectQueries.forEach((result, index) => {
      const id = projectIds[index];
      if (result.data) {
        map[`projects-${id}`] = result.data;
      }
    });

    jobQueries.forEach((result, index) => {
      const id = jobIds[index];
      if (result.data) {
        map[`jobs-${id}`] = result.data;
      }
    });

    jobExtensionQueries.forEach((result, index) => {
      const id = jobExtensionIds[index];
      if (result.data) {
        map[`job_extensions-${id}`] = result.data;
      }
    });

    // Tickets mapping without API: simply expose id so router can build URL
    ticketIds.forEach((id) => {
      map[`tickets-${id}`] = { id };
    });

    billableIds.forEach((id) => {
      map[`billables-${id}`] = { id };
    });

    return map;
  }, [
    projectQueries,
    jobQueries,
    jobExtensionQueries,
    projectIds,
    jobIds,
    jobExtensionIds,
    ticketIds,
    billableIds,
  ]);

  return dataMap;
}
